import {
  Component,
  OnInit,
  Input,
  Output,
  EventEmitter,
  OnDestroy
} from '@angular/core';
import { ReportsService } from '../../../core/reports.service';
import { of, Subscription } from 'rxjs';
import { tap, catchError } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';
import { PermissionsService } from '../../../../core/permissions.service';
import { ReportOptionsService } from '../report-options.service';

interface SimpleSetVehicle {
  vehicleId: number | null;
  setId: number | null;
  name: string;
}

@Component({
  selector: 'app-set-selector',
  templateUrl: './set-selector.component.html',
  styleUrls: ['./set-selector.component.scss']
})
export class SetSelectorComponent implements OnInit, OnDestroy {
  sets: SimpleSetVehicle[] = [];
  setsOptions: { label: string; value: { vehicleId: number | null; setId: number | null } }[] = [];
  selectedSet: { label: string; value: { vehicleId: number | null; setId: number | null } } | null = null;
  setsIsLoading = true;
  private groupSubscription: Subscription;

  @Input() type: string;
  @Input() reportId: number;
  @Input() label = this.translateService.instant('reports.vehicle');
  @Input() allVehiclesText = this.translateService.instant('reports.all-vehicles');
  @Input() placeholder = this.translateService.instant('reports.select-vehicle');
  @Input() selectedSetName: string;
  @Input() selectedVehicleId: number | null;
  @Input() selectedSetId: number | null;
  @Input() allSetsOption = false;
  @Input() disableFilterByType = true;
  @Input() dropdownClass = 'dropdown--gray';
  @Input() dropdownIconClass = 'fa fa-chevron-down';

  @Output() selectedSetNameChange = new EventEmitter<string>();
  @Output() selectedSetIdChange = new EventEmitter<number | null>();
  @Output() selectedVehicleIdChange = new EventEmitter<number | null>();

  constructor(
    private reportsService: ReportsService,
    private translateService: TranslateService,
    public permissionsService: PermissionsService,
    private reportOptionsService: ReportOptionsService
  ) {}

  /* ────────────────────────────────────────────── lifecycle ── */
  ngOnInit(): void {
    // console.log('🔧 Init SetSelector:');
    // console.log('➡️ selectedSetId:', this.selectedSetId, 'selectedVehicleId:', this.selectedVehicleId);
    this.loadSets();
  }

  ngOnDestroy(): void {
    if (this.groupSubscription) {
      this.groupSubscription.unsubscribe();
    }
  }

  /* ────────────────────────────────────────────── data loading ── */
  private loadSets(): void {
    this.reportsService
      .getSets(this.reportId)
      .pipe(
        tap(
          function (data: any[]) {
            this.sets = data || [];
            this.setsOptions = this.prepareSetsOptions();
            this.selectSet();
            this.setsIsLoading = false;
          }.bind(this)
        ),
        catchError(
          function (error) {
            console.error('Błąd podczas ładowania zestawów:', error);
            this.sets = [];
            this.setsOptions = [];
            this.setsIsLoading = false;
            return of();
          }.bind(this)
        )
      )
      .subscribe();
  }

  /* ────────────────────────────────────────────── helpers ── */
  private prepareSetsOptions() {
    const list = this.filterSetsList();
    if (this.allSetsOption && list.length > 1) {
      return this.getAllSetsOption().concat(list);
    }
    return list;
  }

  private getAllSetsOption() {
    return [
      {
        label: this.allVehiclesText,
        value: {
          vehicleId: 0,
          setId: 0
        }
      }
    ];
  }

  private filterSetsList() {
    return this.sets
      .filter(
        function (set) {
          return !this.reportsService.filterByGroupIsEnable || this.filterByGroup(set);
        }.bind(this)
      )
      .map(function (set) {
        return {
          label: set.name,
          value: {
            vehicleId: set.vehicleId,
            setId: set.setId
          }
        };
      });
  }

  private filterByGroup(set: any) {
    return this.reportsService.filterGroupIdArray.indexOf(set.id) !== -1;
  }
  private selectSet(): void {
    const setId = this.selectedSetId;
    const vehicleId = this.selectedVehicleId;

    // console.log('📦 Dostępne zestawy:', this.sets);
    // console.log('➡️ setId:', setId, 'vehicleId:', vehicleId);

    let matchedOption = null;

    if (setId !== null && setId !== undefined) {
      for (let i = 0; i < this.setsOptions.length; i++) {
        if (this.setsOptions[i].value.setId === setId) {
          matchedOption = this.setsOptions[i];
          // console.log('✅ Dopasowano opcję po setId:', matchedOption);
          break;
        }
      }
    }

    if (!matchedOption && vehicleId !== null && vehicleId !== undefined) {
      for (let j = 0; j < this.setsOptions.length; j++) {
        if (this.setsOptions[j].value.vehicleId === vehicleId) {
          matchedOption = this.setsOptions[j];
          // console.log('✅ Dopasowano opcję po vehicleId:', matchedOption);
          break;
        }
      }
    }

    if (matchedOption) {
      this.selectedSet = matchedOption;
      this.selectedSetName = matchedOption.label;
      // console.log('🎯 Wybrany zestaw (dropdown model):', matchedOption);
    } else {
      this.selectedSet = null;
      this.selectedSetName = '';
      console.warn('🔴 Nie znaleziono pasującego zestawu – dropdown pusty');
    }
  }

  onSelectedSetNameChange(): void {
    if (this.selectedSet && this.selectedSet.value) {
      const { setId, vehicleId } = this.selectedSet.value;

      this.selectedSetName = this.selectedSet.label;
      this.selectedSetIdChange.emit(setId);
      this.selectedVehicleIdChange.emit(vehicleId);
      this.selectedSetNameChange.emit(this.selectedSet.label);
      this.reportOptionsService.setName = this.selectedSet.label;

      // console.log('🟢 Wybrano z dropdowna:', this.selectedSet.label, '| setId:', setId, ', vehicleId:', vehicleId);
    } else {
      this.selectedSetIdChange.emit(-1);
      this.selectedVehicleIdChange.emit(-1);
      this.selectedSetNameChange.emit('');
      // console.warn('🔴 Brak wybranego zestawu z dropdowna.');
    }
  }
}
